import { MessageCircle, FolderKanban, Brain, Check, X } from "lucide-react";
import { Reveal, Chapter, SectionHeading } from "./Reveal";

const STEPS = [
  { n: "1", icon: MessageCircle, t: "Message", d: "Talk naturally, just like any other messaging app." },
  { n: "2", icon: FolderKanban, t: "Project", d: "When a conversation needs more structure, make it a Project." },
  { n: "3", icon: Brain, t: "Deyem remembers", d: "Important information stays organised and accessible." },
];

const COLS = [
  {
    title: "Traditional messaging",
    good: "Great for conversation.",
    bad: ["Decisions", "Tasks", "Deadlines", "Documents", "Measurements"].map((x) => `Poor at remembering ${x.toLowerCase()}`),
  },
  {
    title: "Project-management software",
    good: "Great for organisation.",
    bad: ["Requires setup", "Requires training", "Manual data entry", "Constant management"],
  },
];

export const HowItWorks = () => (
  <section id="how-it-works" className="bg-white py-28 sm:py-36" data-testid="how-it-works-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <Chapter index="13" label="How it works" />
      <div className="mt-8 max-w-3xl">
        <Reveal delay={0.1}>
          <SectionHeading>Three steps. No manual.</SectionHeading>
        </Reveal>
      </div>

      <div className="mt-16 grid md:grid-cols-3 gap-5">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={0.1 + i * 0.12}>
            <div className="relative rounded-3xl border border-diem-line bg-white p-8 h-full overflow-hidden group hover:border-diem-blue/40 transition-colors duration-300" data-testid={`how-it-works-step-${s.n}`}>
              <span className="font-display text-[7rem] leading-none font-medium tracking-tighter text-diem-paper absolute -top-4 -right-2 select-none group-hover:text-diem-blue/10 transition-colors duration-500">
                {s.n}
              </span>
              <span className="relative w-12 h-12 rounded-2xl bg-diem-blue/10 flex items-center justify-center">
                <s.icon className="w-6 h-6 text-diem-blue" />
              </span>
              <h3 className="relative mt-6 font-display text-2xl font-semibold tracking-tight text-diem-ink">{s.t}</h3>
              <p className="relative mt-3 text-[15px] text-diem-slate leading-relaxed">{s.d}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-24">
        <Reveal>
          <h3 className="font-display text-3xl sm:text-4xl font-medium tracking-tighter text-diem-ink max-w-2xl">
            Conversation <span className="text-diem-blue">+</span> organisation. Finally in the same place.
          </h3>
        </Reveal>
        <div className="mt-10 grid md:grid-cols-3 gap-5" data-testid="comparison-grid">
          {COLS.map((c, i) => (
            <Reveal key={c.title} delay={0.1 + i * 0.1}>
              <div className="rounded-3xl border border-diem-line bg-diem-paper p-7 h-full">
                <p className="font-display text-xl font-semibold tracking-tight text-diem-ink">{c.title}</p>
                <p className="mt-3 flex items-center gap-2 text-sm font-semibold text-diem-ok">
                  <Check className="w-4 h-4" /> {c.good}
                </p>
                <div className="mt-4 space-y-2">
                  {c.bad.map((b) => (
                    <p key={b} className="flex items-center gap-2 text-sm text-diem-muted">
                      <X className="w-4 h-4 text-diem-err/70 shrink-0" /> {b}
                    </p>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.3}>
            <div className="rounded-3xl bg-diem-ink p-7 h-full text-white shadow-[0_30px_70px_-16px_rgba(15,23,42,0.4)] relative overflow-hidden" data-testid="comparison-diem-card">
              <div className="absolute inset-0 opacity-25" style={{ background: "radial-gradient(400px 220px at 90% 0%, #0055FF, transparent 65%)" }} />
              <div className="relative">
                <p className="font-display text-xl font-semibold tracking-tight">Deyem<span className="text-diem-sky">.</span></p>
                <p className="mt-3 flex items-center gap-2 text-sm font-semibold text-diem-sky">
                  <Check className="w-4 h-4" /> Conversation + organisation
                </p>
                <div className="mt-4 space-y-2">
                  {["Message like you always have", "Important stuff organises itself", "Zero training, zero data entry", "Nothing important gets lost"].map((g) => (
                    <p key={g} className="flex items-center gap-2 text-sm text-white/75">
                      <Check className="w-4 h-4 text-diem-ok shrink-0" /> {g}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
